import { useParams } from '@tanstack/react-router'
import { message } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { MultiSelect } from 'components/form/inputs/MultiSelect'
import { NumberInput } from 'components/form/inputs/NumberInput'
import { TextInput } from 'components/form/inputs/TextInput'
import { useRolodexEdit } from 'features/fde'
import {
  formValidationStyles,
  StyledFormSubmitButton,
} from 'features/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { RightSideDrawerProps } from 'features/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'features/rightDrawer/utils/RightDrawerProvider'
import { generateEntrySlug, useRolodexEntries } from 'features/rolodex'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  RolodexEntryPatchJson,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  ${formValidationStyles}
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.forecast.background3};
`

export const CreateSystemDrawerProps: RightSideDrawerProps = {
  title: 'Create System',
}

type FormState = {
  name: string
  description?: string
  type: string
  owner: string
  criticality?: number
  groupings: string[]
  unitIds: string[]
}

const initialState: FormState = {
  name: '',
  description: '',
  type: '',
  owner: '',
  criticality: 3,
  groupings: [],
  unitIds: [],
}

export const CreateSystemDrawer: React.FC = () => {
  const { siteID } = useParams({ strict: false })
  const [form, setForm] = useState<FormState>(initialState)
  const [isSaving, setIsSaving] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()

  const { closeDrawer, drawerState } = useRightDrawer()

  // Load units for the site to select from
  const { units, forceRefresh } = useRolodexEntries(
    siteID
      ? {
          type: RolodexEntryType.UNIT,
          tags: { site: siteID },
        }
      : undefined
  )

  // If organizationId is not provided, look up the site to infer it
  const { getSite } = useRolodexEntries(
    siteID
      ? {
          type: RolodexEntryType.SITE,
          ids: [siteID],
        }
      : undefined
  )

  const site = useMemo(() => getSite(siteID), [getSite, siteID])

  // Reset form when drawer opens
  useEffect(() => {
    setForm(initialState)
  }, [drawerState])

  const unitOptions = useMemo(
    () =>
      (units || []).map((u) => ({ label: u.data.name || u.id, value: u.id })),
    [units]
  )

  const { rolodexPut, rolodexPatch } = useRolodexEdit()

  const handleSave = async () => {
    if (!form.name.trim()) {
      messageApi.error('Name is required')
      return
    }

    if (!site) {
      messageApi.error('Site not found')
      return
    }

    setIsSaving(true)
    try {
      // Create the system entry
      const slug = generateEntrySlug(form.name)
      const systemPut: RolodexEntryPutJson = {
        id: slug,
        type: RolodexEntryType.SYSTEM,
        data: {
          name: form.name,
          description: form.description || undefined,
          type: form.type,
          owner: form.owner,
          criticality: form.criticality,
          groupings: form.groupings || [],
        },
        tags: {
          site: site.id,
          organization: site.tags.organization,
        },
      }

      await rolodexPut([systemPut])

      // Patch selected units to include this system in their data.systems array
      if (form.unitIds.length > 0) {
        const patches: RolodexEntryPatchJson[] = form.unitIds.map((unitId) => {
          return {
            id: unitId,
            data: { systems: [slug] },
          }
        })
        await rolodexPatch(patches)
      }

      await forceRefresh()
      messageApi.success('System created successfully!')

      closeDrawer()
    } catch (error) {
      messageApi.error('Failed to create system. Please try again.')
      console.error('Create system error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      {contextHolder}
      <FormContainer>
        <TextInput
          $label='Name'
          placeholder='e.g., Boiler Feedwater'
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <TextInput
          $label='Description'
          placeholder='Optional description'
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
        />
        <TextInput
          $label='Type'
          placeholder='e.g., process, utility, electrical'
          value={form.type}
          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
        />
        <TextInput
          $label='Owner'
          placeholder='e.g., Operations'
          value={form.owner}
          onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))}
        />
        <NumberInput
          $label='Criticality'
          placeholder='1-5'
          value={form.criticality}
          onChange={(v) =>
            setForm((f) => ({ ...f, criticality: Number(v) || undefined }))
          }
        />
        <MultiSelect
          $label='Units in this System'
          placeholder='Search and select units'
          options={unitOptions}
          onValueChange={(vals) => setForm((f) => ({ ...f, unitIds: vals }))}
        />
      </FormContainer>
      <ButtonContainer>
        <CantileverButton onClick={closeDrawer} disabled={isSaving}>
          Cancel
        </CantileverButton>
        <StyledFormSubmitButton
          disabled={isSaving || !form.name}
          onClick={handleSave}>
          {isSaving ? 'Creating...' : 'Create'}
        </StyledFormSubmitButton>
      </ButtonContainer>
    </>
  )
}

export default CreateSystemDrawer
